import { FilterPageParams, UserMgmtInfo, UserMgmtList } from 'common-types/src/card';
import { IPageListApiResponse } from 'common-types/src/fire';
import api, { ApiIndicator } from '../../api';

const USER_URL = '/management/users';
export type UserMgmtAPIResponse = IPageListApiResponse<UserMgmtList>;

/**
 * 관리 > 사용자 관리 목록 불러오기
 * [GET] /management/users
 * @param params
 * @returns boolean
 */
export const getUserMgmtList = async <T = UserMgmtAPIResponse>(
  apiInstance: ApiIndicator,
  params: FilterPageParams & { permissionUnitId?: number | string | null },
  signal?: AbortSignal
): Promise<T> => {
  const { data } = await api[apiInstance].get<T>(`${USER_URL}`, { params, signal });
  return data;
};

/**
 * 사용자 비밀번호 변경
 * [POST] /management/users/password
 * @param currentPassword
 * @param newPassword
 * @returns
 */
export const updateMyPasswordByToken = async (
  apiInstance: ApiIndicator,
  {
    token,
    myPassword,
    updatePassword,
  }: { token: string; myPassword: string; updatePassword: string }
) => {
  const { data } = await api[apiInstance].post(`${USER_URL}/mypassword/token`, {
    token,
    myPassword,
    updatePassword,
  });
  return data;
};

/**
 * 관리 > 사용자 관리 접근 허용
 * [POST] /management/users/activated
 * @param userIds
 * @returns boolean
 */
export const activatedUserMgmt = async <T = UserMgmtAPIResponse>(
  apiInstance: ApiIndicator,
  userIds: string
) => {
  await api[apiInstance].post<T>(`${USER_URL}/activated?userIds=${userIds}`);
};

/**
 * 관리 > 사용자 관리 > 사용자 다중 삭제
 * [POST] /management/users/delete
 * @param userIds
 * @returns boolean
 */
export const deleteUsersMgmt = async <T = UserMgmtAPIResponse>(
  apiInstance: ApiIndicator,
  userIds: string[]
) => {
  await api[apiInstance].post<T>(`${USER_URL}/delete`, {
    userIds,
  });
};

/**
 * 관리 > 사용자 관리 접근 차단
 * [POST] /management/users/inactivated
 * @param userIds
 * @returns boolean
 */
export const inactivatedUserMgmt = async <T = UserMgmtAPIResponse>(
  apiInstance: ApiIndicator,
  userIds: string
) => {
  await api[apiInstance].post<T>(`${USER_URL}/inactivated?userIds=${userIds}`);
};

/**
 * 관리 > 사용자 관리 비밀번호 초기화
 * [POST] /management/users/{userId}/password/init
 * @param userIds
 * @returns boolean
 */
export const initUserPassword = async <T = UserMgmtAPIResponse>(
  apiInstance: ApiIndicator,
  userIds: string
) => {
  await api[apiInstance].post<T>(`${USER_URL}/${userIds}/password/init`);
};

/**
 * 관리 > 사용자 관리 내보내기
 * /management/users/export
 */
export const exportUserMgmtList = async (
  apiInstance: ApiIndicator,
  keyword?: string,
  filter?: string
) => {
  const URL = `/management/users/export?keyword=${keyword}&filter=${filter}`;
  const response = await api[apiInstance]
    .get(URL, {
      responseType: 'blob',
    })
    .catch((err) => {
      return err.response;
    });
  return response;
};

/**
 * 관리 > 사용자 관리의 수정하기 팝업
 * [POST] /management/users
 * @param body
 * @returns boolean
 */
export const editUserMgmt = async (
  apiInstance: ApiIndicator,
  originId: string,
  body: UserMgmtInfo
) => {
  await api[apiInstance].post<UserMgmtAPIResponse>(`${USER_URL}/${originId}`, body);
};

/**
 * 관리 > 사용자 관리 비밀번호 초기화 조회
 * [GET] /management/users/password
 * @param params
 * @returns boolean
 */
export const initUserPasswordPrint = async (apiInstance: ApiIndicator) => {
  const { data } = await api[apiInstance].get<string>(`${USER_URL}/password`);
  return data;
};

/**
 * 관리 > 사용자 관리 등록하기 팝업 (단일 등록)
 * [POST] /management/users
 * @param body
 * @returns boolean
 */
export const addUserMgmt = async (
  apiInstance: ApiIndicator,
  body: UserMgmtInfo
): Promise<UserMgmtAPIResponse> => {
  const { data } = await api[apiInstance].post<UserMgmtAPIResponse>(`${USER_URL}`, body);
  return data;
};

/**
 * 사용자 설정 > 내 정보 수정
 * [PUT] /management/users/myinfo
 * @returns boolean
 */
export const myInfoChange = async (apiInstance: ApiIndicator, body: UserMgmtInfo) => {
  const { userId, itcoEmail, userNm, deptNm, dutyNm } = body;
  await api[apiInstance].post<UserMgmtAPIResponse>(`${USER_URL}/myinfo`, {
    userId,
    itcoEmail,
    userNm,
    deptNm,
    dutyNm,
  });
};

/**
 * 사용자 설정 > 비밀번호 변경
 * [PUT] /management/users/mypassword
 * @returns boolean
 */
export const myPasswordChange = async (
  apiInstance: ApiIndicator,
  myPassword: string,
  updatePassword: string
) => {
  return api[apiInstance].post<UserMgmtAPIResponse>(`${USER_URL}/mypassword`, {
    myPassword,
    updatePassword,
  });
};

/**
 * 사용자 업데이트 시간 조회
 * 예시: [GET] /management/users/updatetime
 */
export async function getUserUpdateTime(
  apiInstance: ApiIndicator
): Promise<{ updateTime: string }> {
  const { data } = await api[apiInstance].get<{ updateTime: string }>(
    '/management/users/updatetime'
  );
  return data;
}
